import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

@Component({
  selector: 'image-viewer',
  standalone: true,
  imports: [CommonModule, NzModalModule, NzButtonModule, NzIconModule, NzToolTipModule, NzDividerModule, I18nPipe, TestIdDirective],
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.less']
})
export class ImageViewerComponent implements OnInit, OnDestroy {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  @Input() visible = false;
  @Input() imageUrl = '';
  @Input() title = this.i18n.fanyi('avatar.viewer.title');
  @Input() showToolbar = true;
  @Input() showDownload = true;
  @Input() showRotate = true;
  @Input() showZoom = true;
  @Input() showFullscreen = true;
  @Input() minScale = 0.1;
  @Input() maxScale = 5;
  @Input() scaleStep = 0.1;

  @Output() readonly visibleChange = new EventEmitter<boolean>();
  @Output() readonly close = new EventEmitter<void>();

  // 图片变换状态
  scale = 1;
  rotation = 0;
  translateX = 0;
  translateY = 0;
  isDragging = false;
  lastMouseX = 0;
  lastMouseY = 0;
  isFullscreen = false;

  ngOnInit(): void {
    this.resetTransform();
  }

  ngOnDestroy(): void {
    this.exitFullscreen();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.visible) return;

    switch (event.key) {
      case 'Escape':
        this.handleClose();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case 'r':
      case 'R':
        this.rotateRight();
        break;
      case 'f':
      case 'F':
        this.toggleFullscreen();
        break;
      case '0':
        this.resetTransform();
        break;
    }
  }

  handleClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.close.emit();
    this.resetTransform();
    this.exitFullscreen();
  }

  // 缩放功能
  zoomIn(): void {
    if (this.scale < this.maxScale) {
      this.scale = Math.min(this.scale + this.scaleStep, this.maxScale);
    }
  }

  zoomOut(): void {
    if (this.scale > this.minScale) {
      this.scale = Math.max(this.scale - this.scaleStep, this.minScale);
    }
  }

  // 旋转功能
  rotateLeft(): void {
    this.rotation -= 90;
  }

  rotateRight(): void {
    this.rotation += 90;
  }

  // 重置变换
  resetTransform(): void {
    this.scale = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;
  }

  // 适应窗口
  fitToWindow(): void {
    this.resetTransform();
    // 可以根据容器大小计算合适的缩放比例
  }

  // 原始大小
  actualSize(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
  }

  // 全屏功能
  toggleFullscreen(): void {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  private enterFullscreen(): void {
    const element = document.documentElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
      this.isFullscreen = true;
    }
  }

  private exitFullscreen(): void {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen();
      this.isFullscreen = false;
    }
  }

  // 下载图片
  downloadImage(): void {
    if (!this.imageUrl) return;

    const link = document.createElement('a');
    link.href = this.imageUrl;
    const filename = this.imageUrl.startsWith('data:') ? 'test.jpg' : this.getImageName();
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getImageName(): string {
    const url = this.imageUrl;
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return filename || 'image.jpg';
  }

  // 鼠标拖拽功能
  onMouseDown(event: MouseEvent): void {
    if (event.button === 0) {
      // 左键
      this.isDragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      event.preventDefault();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging && this.visible) {
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;

      this.translateX += deltaX;
      this.translateY += deltaY;

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  // 鼠标滚轮缩放
  onWheel(event: WheelEvent): void {
    event.preventDefault();

    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  // 获取图片变换样式
  getImageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale}) rotate(${this.rotation}deg)`;
  }

  // 获取缩放百分比
  getScalePercentage(): number {
    return Math.round(this.scale * 100);
  }
}
